import { MarginalAbatementCostModel } from "./MaginalAbatementResult";

export type ProjectRegisterGroupBy = "Name" | "Type" | "Category";

type ProjectRegisterSourceRow = MarginalAbatementCostModel & {
  financialLifetime?: number | null;
};

export type ProjectRegisterTableRow = Omit<
  ProjectRegisterSourceRow,
  | "reductionMeasureId"
  | "businessUnit"
  | "startYear"
  | "pace"
  | "execution"
  | "endYear"
  | "financialLifetime"
> & {
  reductionMeasureId: number | string;
  businessUnit?: string | null;
  startYear?: number | string | null;
  pace?: number | string | null;
  execution?: number | string | null;
  endYear?: number | string | null;
  financialLifetime?: number | string | null;
};

const GROUP_LABEL_BY_VALUE: Record<ProjectRegisterGroupBy, string> = {
  Name: "Measure Name",
  Type: "Measure Type",
  Category: "Measure Category",
};

const toNumber = (value: number | string | null | undefined) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const normalizedValue = value.replace(/,/g, "").trim();
    if (normalizedValue === "") {
      return 0;
    }

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  return 0;
};

const getGroupValue = (
  row: ProjectRegisterSourceRow,
  groupBy: ProjectRegisterGroupBy
) => {
  if (groupBy === "Type") {
    return row.measureType?.trim() || "Unspecified";
  }

  if (groupBy === "Category") {
    return row.measureCategory?.trim() || "Unspecified";
  }

  return row.measureName?.trim() || "Unspecified";
};

const getComparableValue = (value: unknown) => {
  if (value == null || value === "") {
    return null;
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }

  return null;
};

const mergeDisplayValue = <T,>(
  currentValue: T | string | null | undefined,
  nextValue: T | null | undefined
) => {
  const normalizedCurrentValue = getComparableValue(currentValue);
  const normalizedNextValue = getComparableValue(nextValue);

  if (normalizedCurrentValue == null) {
    return nextValue ?? null;
  }

  if (normalizedNextValue == null) {
    return currentValue;
  }

  if (normalizedCurrentValue === normalizedNextValue) {
    return currentValue;
  }

  return "Multiple";
};

export const getProjectRegisterGroupHeader = (groupBy: ProjectRegisterGroupBy) =>
  GROUP_LABEL_BY_VALUE[groupBy];

export const getProjectRegisterRows = (
  marginalAbatementCost: MarginalAbatementCostModel[],
  groupBy: ProjectRegisterGroupBy
): ProjectRegisterTableRow[] => {
  const sourceRows = marginalAbatementCost as ProjectRegisterSourceRow[];

  if (groupBy === "Name") {
    return sourceRows.map((row) => ({
      ...row,
      reductionMeasureId: row.reductionMeasureId,
    }));
  }

  const groupedRows = sourceRows.reduce<Record<string, ProjectRegisterTableRow>>(
    (accumulator, row) => {
      const groupValue = getGroupValue(row, groupBy);
      const existingRow = accumulator[groupValue];

      if (!existingRow) {
        accumulator[groupValue] = {
          reductionMeasureId: `${groupBy}-${groupValue}`,
          measureName: groupValue,
          measureType: row.measureType,
          measureCategory: row.measureCategory,
          businessUnit: row.businessUnit ?? null,
          startYear: row.startYear ?? null,
          pace: row.pace ?? null,
          execution: row.execution ?? null,
          endYear: row.endYear ?? null,
          emissionReduction: toNumber(row.emissionReduction),
          annualNPV: toNumber(row.annualNPV),
          capExInvestment: toNumber(row.capExInvestment),
          mac: toNumber(row.mac),
          averageAnnualROI: toNumber(row.averageAnnualROI),
          isAverageAnnualRoiPresent: row.isAverageAnnualRoiPresent === true,
          financialLifetime: row.financialLifetime ?? null,
        };
        return accumulator;
      }

      existingRow.businessUnit = mergeDisplayValue(
        existingRow.businessUnit,
        row.businessUnit ?? null
      );
      existingRow.startYear = mergeDisplayValue(
        existingRow.startYear,
        row.startYear ?? null
      );
      existingRow.pace = mergeDisplayValue(existingRow.pace, row.pace ?? null);
      existingRow.execution = mergeDisplayValue(
        existingRow.execution,
        row.execution ?? null
      );
      existingRow.endYear = mergeDisplayValue(
        existingRow.endYear,
        row.endYear ?? null
      );
      existingRow.financialLifetime = mergeDisplayValue(
        existingRow.financialLifetime,
        row.financialLifetime ?? null
      );

      existingRow.emissionReduction =
        toNumber(existingRow.emissionReduction) + toNumber(row.emissionReduction);
      existingRow.annualNPV =
        toNumber(existingRow.annualNPV) + toNumber(row.annualNPV);
      existingRow.capExInvestment =
        toNumber(existingRow.capExInvestment) + toNumber(row.capExInvestment);
      existingRow.mac = toNumber(existingRow.mac) + toNumber(row.mac);
      existingRow.averageAnnualROI =
        toNumber(existingRow.averageAnnualROI) + toNumber(row.averageAnnualROI);
      existingRow.isAverageAnnualRoiPresent =
        existingRow.isAverageAnnualRoiPresent === true || row.isAverageAnnualRoiPresent === true;

      return accumulator;
    },
    {}
  );

  return Object.values(groupedRows);
};

export const getProjectRegisterTotals = (
  rows: ProjectRegisterTableRow[]
): ProjectRegisterTableRow => ({
  reductionMeasureId: "Total",
  measureName: "",
  measureType: "",
  measureCategory: "",
  businessUnit: null,
  startYear: null,
  pace: null,
  execution: null,
  endYear: null,
  financialLifetime: null,
  emissionReduction: rows.reduce(
    (total, row) => total + toNumber(row.emissionReduction),
    0
  ),
  annualNPV: rows.reduce((total, row) => total + toNumber(row.annualNPV), 0),
  capExInvestment: rows.reduce((total, row) => total + toNumber(row.capExInvestment ?? row.capExInvestment), 0),
  mac: rows.reduce((total, row) => total + toNumber(row.mac), 0),
  averageAnnualROI: rows.reduce(
    (total, row) => total + toNumber(row.averageAnnualROI),
    0
  ),
  isAverageAnnualRoiPresent: rows.some((row) => row.isAverageAnnualRoiPresent === true),
});